import React, { useCallback } from 'react';
import { Droppable, type DragItem } from '../dnd';
import { PlacedInputChip } from './PlacedInputChip';
import { applyDropToInput } from './chip-drop';
import { getFractionKey } from '../../utils/fraction-keys';
import { useSound } from '../sounds';
import type { ValidationStatus } from './types';

export interface FractionInputProps {
  widgetId: string;
  numerator: number | null;
  denominator: number | null;
  values: Record<string, string>;
  droppedInputs: Set<string>;
  validationStatus?: ValidationStatus;
  numValidationStatus?: ValidationStatus;
  denValidationStatus?: ValidationStatus;
  onChange: (widgetId: string, value: string, fromDrop?: boolean, sourceOptionId?: string) => void;
  onFocus?: (widgetId: string) => void;
  placeholder?: string;
}

export const FractionInput: React.FC<FractionInputProps> = ({
  widgetId,
  numerator,
  denominator,
  values,
  droppedInputs,
  validationStatus = null,
  numValidationStatus = null,
  denValidationStatus = null,
  onChange,
  onFocus,
  placeholder,
}) => {
  const playSound = useSound();
  const numKey = getFractionKey(widgetId, 'num');
  const denKey = getFractionKey(widgetId, 'den');

  const numValue = values[numKey] ?? '';
  const denValue = values[denKey] ?? '';
  const statusClass = validationStatus ? ` widget--${validationStatus}` : '';

  const handleDrop = useCallback((subKey: string) => (item: DragItem) => {
    return applyDropToInput(subKey, item, onChange);
  }, [onChange]);

  const renderSlot = (
    subKey: string,
    fixedValue: number | null,
    currentValue: string,
    label: string,
    slotStatus: ValidationStatus
  ) => {
    if (fixedValue !== null) {
      return (
        <span className="fraction-fixed">{fixedValue}</span>
      );
    }

    const isDroppedHere = droppedInputs.has(subKey);
    // Falls back to whole-widget status when per-slot not available (e.g. expression-mode validation).
    const slotClass = slotStatus ? ` widget--${slotStatus}` : statusClass;

    return (
      <Droppable
        id={subKey}
        accepts={['drag-option', 'placed-chip']}
        onDrop={handleDrop(subKey)}
        className="fraction-slot-droppable"
      >
        {isDroppedHere && currentValue !== '' ? (
          <PlacedInputChip
            widgetId={subKey}
            value={currentValue}
            className={`fraction-chip${slotClass}`}
            onRemove={() => onChange(subKey, '', false)}
          />
        ) : (
          <input
            id={`${subKey}-input`}
            type="text"
            className={`inline-input fraction-input${slotClass}`}
            value={currentValue}
            onChange={(e) => onChange(subKey, e.target.value, false)}
            onMouseDown={() => playSound('input-select')}
            onFocus={() => onFocus?.(subKey)}
            maxLength={placeholder ? Math.max(20, placeholder.length) : 4}
            size={placeholder ? placeholder.length : undefined}
            aria-label={label}
            // Single placeholder shown on both num and den slots as a general hint.
            placeholder={placeholder}
          />
        )}
      </Droppable>
    );
  };

  return (
    <span className="inline-widget fraction" role="math" aria-label={`fraction ${widgetId}`}>
      <span className="fraction-numerator">
        {renderSlot(numKey, numerator, numValue, 'numerator', numValidationStatus)}
      </span>
      <span className="fraction-bar" aria-hidden="true" />
      <span className="fraction-denominator">
        {renderSlot(denKey, denominator, denValue, 'denominator', denValidationStatus)}
      </span>
    </span>
  );
};
